import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { ContactFilterRequest } from '../../extViewmodels/sdk/models/requests/ContactFilterRequest';
import { IAICampaignSendFrom } from '../../models/AICampaign';
import { useErrorMessages, useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateAICampaignDraft, setAICampaignDraft } from './useAICampaignDraft';

const SAVE_KEY = 'aiCampaign/draft/save';

/** For telling whether a draft has edits on their way to the server. */
export const aiCampaignDraftSaveKey = (campaignId: string) => [SAVE_KEY, campaignId];

/**
 * Each PUT replaces a whole section, so saves of the same draft run one after another rather than racing, and a
 * failed one reloads the draft so the panel shows what was actually saved.
 */
const useDraftSection = <TBody>(campaignId: string, section: 'audience' | 'sendFrom') => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	return useMutation({
		mutationFn: (body: TBody) =>
			userSession.webServiceHelper.callAsync<Api.ICampaign>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AICampaign/${encodeURIComponent(campaignId)}/${section}`,
				}),
				'PUT',
				body
			),
		mutationKey: aiCampaignDraftSaveKey(campaignId),
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
			invalidateAICampaignDraft(campaignId);
		},
		onSuccess: setAICampaignDraft,
		scope: { id: `${SAVE_KEY}/${campaignId}` },
	});
};

export const useUpdateAICampaignAudience = (campaignId: string) =>
	useDraftSection<ContactFilterRequest>(campaignId, 'audience');

export const useUpdateAICampaignSendFrom = (campaignId: string) =>
	useDraftSection<IAICampaignSendFrom>(campaignId, 'sendFrom');
